/**
 * TabbedDetailsModal Component
 * 
 * Modal with tab navigation for displaying detailed information.
 * Commonly used for viewing item details with multiple sections.
 */

import React, { useEffect } from 'react';
import { X, type LucideIcon } from 'lucide-react';

export interface Tab {
  /** Unique tab identifier */
  id: string;
  /** Tab label */
  label: string;
  /** Tab icon */
  icon: LucideIcon;
}

export interface TabbedDetailsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Optional header icon element */
  headerIcon?: React.ReactNode;
  /** Optional banner below header (e.g., warnings) */
  headerBanner?: React.ReactNode;
  /** Tab configuration */
  tabs: Tab[];
  /** Currently active tab id */
  activeTab: string;
  /** Tab change handler */
  onTabChange: (tabId: string) => void;
  /** Tab content */
  children: React.ReactNode;
  /** Optional footer content */
  footer?: React.ReactNode;
}

export function TabbedDetailsModal({
  isOpen,
  onClose,
  title,
  subtitle,
  headerIcon,
  headerBanner,
  tabs,
  activeTab,
  onTabChange,
  children,
  footer,
}: TabbedDetailsModalProps) {
  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0" 
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal content */}
      <div 
        className="relative bg-white rounded-xl max-w-4xl w-full h-[80vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {headerIcon}
              <div>
                <h2 id="modal-title" className="text-xl font-semibold text-gray-900">
                  {title}
                </h2>
                {subtitle && (
                  <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Banner (e.g., warnings) */}
          {headerBanner}

          {/* Tabs */}
          <div className="mt-6">
            <nav className="flex space-x-8" role="tablist">
              {tabs.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => onTabChange(id)}
                  role="tab"
                  aria-selected={activeTab === id}
                  aria-controls={`tabpanel-${id}`}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div 
          className="p-6 overflow-y-auto flex-1"
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={activeTab}
        >
          {children}
        </div>

        {/* Footer */}
        {footer !== undefined ? (
          footer
        ) : (
          <div className="p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="flex items-center justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * TabContent - Helper component for organizing tab content
 */
export interface TabContentProps {
  children: React.ReactNode;
}

export function TabContent({ children }: TabContentProps) {
  return <div className="space-y-6">{children}</div>;
}

/**
 * DetailSection - Helper component for detail sections within tabs
 */
export interface DetailSectionProps {
  title: string;
  children: React.ReactNode;
}

export function DetailSection({ title, children }: DetailSectionProps) {
  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}

/**
 * DetailGrid - Helper for displaying key-value pairs
 */
export interface DetailGridProps {
  children: React.ReactNode;
  columns?: 1 | 2 | 3;
}

export function DetailGrid({ children, columns = 2 }: DetailGridProps) {
  const colClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-3',
  };
  
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className={`grid ${colClasses[columns]} gap-4`}>
        {children}
      </div>
    </div>
  );
}

/**
 * DetailItem - Individual key-value item
 */
export interface DetailItemProps {
  label: string;
  value: React.ReactNode;
  copyable?: boolean;
  onCopy?: () => void;
}

export function DetailItem({ label, value }: DetailItemProps) {
  return (
    <div>
      <p className="text-sm font-medium text-gray-600">{label}</p>
      <div className="text-gray-900">{value}</div>
    </div>
  );
}

/**
 * QuickStatsGrid - Grid for quick stats in overview tab
 */
export interface QuickStatsGridProps {
  children: React.ReactNode;
  columns?: 2 | 3 | 4;
}

export function QuickStatsGrid({ children, columns = 3 }: QuickStatsGridProps) {
  const colClasses = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-4',
  };
  
  return (
    <div className={`grid ${colClasses[columns]} gap-6`}>
      {children}
    </div>
  );
}

/**
 * QuickStatCard - Individual stat card for overview
 */
export interface QuickStatCardProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  valueColor?: string;
}

export function QuickStatCard({ icon, label, value, valueColor = 'text-gray-900' }: QuickStatCardProps) {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center space-x-3">
        {icon}
        <div>
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className={`text-lg font-semibold ${valueColor}`}>{value}</p>
        </div>
      </div>
    </div>
  );
}
